import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { setDocumentTitle } from '../../utils/DocumentTitle';
// Import SegmentsListModal from leads component
import SegmentsListModal from '../leads/SegmentsListModal';
import { Button, SharedTable, DeleteConfirmationModal } from '../common';

const MailerView = () => {
  // Document title
  useEffect(() => {
    setDocumentTitle('Email Marketing');
  }, []);

  // No dropdown handler needed

  // States
  const [campaigns, setCampaigns] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [leads, setLeads] = useState([]);
  const [segments, setSegments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCampaigns, setSelectedCampaigns] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [campaignsToDelete, setCampaignsToDelete] = useState([]);
  const [previewDevice, setPreviewDevice] = useState('desktop'); // 'desktop' or 'mobile'

  // Pagination state
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });

  // Handle limit change for pagination
  const handleLimitChange = (newLimit) => {
    const limitValue = parseInt(newLimit, 10);
    // Add validation if needed, e.g., ensure it's one of the allowed values
    if (![20, 50, 100].includes(limitValue)) return;

    // Update pagination state, resetting page to 1
    setPagination(prev => ({ ...prev, limit: limitValue, page: 1 }));
    // Note: The useEffect hook will trigger the fetch due to pagination.limit changing
    // Or, you could explicitly call loadData(1, limitValue) if you remove limit from useEffect deps
  };

  // Form states
  const [showNewCampaign, setShowNewCampaign] = useState(false);
  const [showViewCampaign, setShowViewCampaign] = useState(false);
  const [campaignData, setCampaignData] = useState({
    name: '',
    subject: '',
    templateId: '',
    content: '',
    segmentId: '',
    scheduledDate: '',
    status: 'draft',
    senderId: '', // Added sender profile ID
    tracking_enabled: true // Enable tracking by default
  });
  const [senderProfiles, setSenderProfiles] = useState([]);
  const [viewCampaignData, setViewCampaignData] = useState(null);
  const [previewMode, setPreviewMode] = useState(false);
  const [showSegmentsModal, setShowSegmentsModal] = useState(false);
  const [segmentLeadCount, setSegmentLeadCount] = useState(null);

  // AI generator states
  const [showAiGenerator, setShowAiGenerator] = useState(false);
  const [showAiRefiner, setShowAiRefiner] = useState(false);
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiRefining, setAiRefining] = useState(false);
  // Removed state for loaded AI provider/model/config status as it's now central
  const [includeCompanyInfo, setIncludeCompanyInfo] = useState(true);
  const [aiPromptData, setAiPromptData] = useState({
    topic: '',
    audience: '',
    goal: '',
    tone: 'professional',
    language: 'english',
    additionalInfo: ''
  });
  const [aiRefinementInstructions, setAiRefinementInstructions] = useState('');

  useEffect(() => {
    // This effect runs when pagination.page changes
    const loadData = async () => {
      try {
        setLoading(true);
        setSelectedCampaigns([]);

        // Get JWT token for API requests
        const token = localStorage.getItem('token');

        // Fetch actual segments data with lead counts
        try {
          const segmentsRes = await axios.get('/api/segments', {
            headers: { 'x-auth-token': token },
            params: {
              include_counts: 'true'
            }
          });
          setSegments(segmentsRes.data || []);
        } catch (segErr) {
          console.error('Error fetching segments:', segErr);
        }

        // Fetch email templates
        try {
          const templatesRes = await axios.get('/api/email/templates', {
            headers: { 'x-auth-token': token }
          });
          setTemplates(templatesRes.data || []);
        } catch (templatesErr) {
          console.error('Error fetching email templates:', templatesErr);
          // Set some default templates if API fails
          setTemplates([
            { id: 1, name: 'Welcome Email', subject: 'Welcome to our community!', body: '<p>Hello {{lead_name}},</p><p>Welcome to our community! We\'re excited to have you join us.</p>' },
            { id: 2, name: 'Course Promotion', subject: 'New courses available for {{lead_name}}', body: '<p>Hello {{lead_name}},</p><p>We have new courses available that might interest you!</p>' },
            { id: 3, name: 'Follow-up', subject: 'Following up on our conversation', body: '<p>Hello {{lead_name}},</p><p>I wanted to follow up on our previous conversation about our services.</p>' }
          ]);
        }

        // Fetch all available email senders (SMTP profiles + SES)
        try {
          const sendersRes = await axios.get('/api/bulk-mailer/senders', {
            headers: { 'x-auth-token': token }
          });
          
          setSenderProfiles(sendersRes.data || []);
        } catch (sendersErr) {
          console.error('Error fetching email senders:', sendersErr);
          setSenderProfiles([]);
        }

        // REMOVED: Fetch Company Settings for AI config - no longer needed

        // Fetch campaigns with pagination
        try {
          const campaignsRes = await axios.get('/api/bulk-mailer/campaigns', {
            headers: { 'x-auth-token': token },
            params: {
              page: pagination.page,
              limit: pagination.limit
            }
          });

          // Process campaign data to include segment names
          const campaignsData = campaignsRes.data.campaigns || [];

          // For each campaign with a segment_id, attach the segment name from the segments we've fetched
          const processedCampaigns = campaignsData.map(campaign => {
            if (campaign.segment_id) {
              const segment = segments.find(s => s.id === campaign.segment_id);
              return {
                ...campaign,
                segmentName: segment ? segment.name : 'Unknown Segment'
              };
            }
            return campaign;
          });

          // Update pagination data
          setPagination({
            ...pagination,
            total: campaignsRes.data.totalCampaigns || processedCampaigns.length,
            pages: campaignsRes.data.totalPages || 1
          });

          setCampaigns(processedCampaigns);
        } catch (campaignsErr) {
          console.error('Error fetching campaigns:', campaignsErr);
          // Empty array if we can't fetch campaigns
          setCampaigns([]);

          // Make sure pagination shows 0 results
          setPagination({
            ...pagination,
            total: 0,
            pages: 0
          });
        }

      } catch (err) {
        console.error('Error loading email marketing data:', err);
        setError('Failed to load data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  // Consider removing pagination.limit dependency if handleLimitChange explicitly fetches
  }, [segments.length, pagination.page, pagination.limit]);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCampaignData({
      ...campaignData,
      [name]: value
    });

    // If template is selected, populate subject and content
    if (name === 'templateId' && value) {
      const selectedTemplate = templates.find(t => t.id === parseInt(value));
      if (selectedTemplate) {
        setCampaignData({
          ...campaignData,
          templateId: value,
          subject: selectedTemplate.subject,
          content: selectedTemplate.body
        });
      }
    }

    // If segment is selected, fetch the lead count
    if (name === 'segmentId' && value) {
      fetchSegmentLeadCount(value);
    }
  };

  // Fetch lead count for a segment
  const fetchSegmentLeadCount = async (segmentId) => {
    if (!segmentId) return;

    try {
      setSegmentLeadCount(null); // Reset while loading

      // Get segment information from already loaded segments array
      const segment = segments.find(s => s.id.toString() === segmentId.toString());
      
      if (segment) {
        // Use the segment data that's already loaded with counts
        setSegmentLeadCount({
          count: segment.lead_count || 0,
          subscribed_count: segment.subscribed_count || 0
        });
      } else {
        // If not found in loaded segments, try to fetch directly
        const token = localStorage.getItem('token');
        const response = await axios.get(`/api/segments/${segmentId}`, {
          headers: { 'x-auth-token': token },
          params: { include_counts: 'true' }
        });

        if (response.data) {
          setSegmentLeadCount({
            count: response.data.lead_count || 0,
            subscribed_count: response.data.subscribed_count || 0
          });
        }
      }
    } catch (err) {
      console.error('Error fetching segment lead count:', err);
      setError('Failed to get segment recipient count');
    }
  };

  // Apply template variables to preview content
  const getPreviewContent = (content, lead) => {
    if (!content || !lead) return content;

    let previewContent = content;
    previewContent = previewContent.replace(/{{lead_name}}/g, lead.name || '');
    previewContent = previewContent.replace(/{{lead_email}}/g, lead.email || '');
    previewContent = previewContent.replace(/{{country}}/g, lead.country || '');

    return previewContent;
  };

  // Handle form submission (create campaign)
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate form
    if (!campaignData.name || !campaignData.subject || !campaignData.content) {
      setError('Please fill in all required fields');
      return;
    }

    // Only require sender profile when actually sending (status: 'sending')
    if (campaignData.status === 'sending' && !campaignData.senderId) {
      setError('Please select a sender profile before sending');
      return;
    }

    // Check if no senders are configured when trying to send
    if (campaignData.status === 'sending' && campaignData.senderId === 'no_senders') {
      setError('Please configure SMTP profiles or Amazon SES in Settings before sending campaigns');
      return;
    }

    // Only require segment selection if not saving as draft
    if (campaignData.status !== 'draft' && !campaignData.segmentId) {
      setError('Please select a segment for recipients');
      return;
    }

    try {
      // Prepare request data
      const requestData = {
        name: campaignData.name,
        subject: campaignData.subject,
        content: campaignData.content,
        text: campaignData.content.replace(/<[^>]*>/g, ''), // Simple HTML to text conversion
        status: campaignData.status,
        scheduledDate: campaignData.scheduledDate || null,
        segmentId: campaignData.segmentId,
        senderId: campaignData.senderId,
        tracking_enabled: campaignData.tracking_enabled
      };

      setLoading(true);

      // If campaign has an ID, it's an existing draft being edited - use PUT
      const token = localStorage.getItem('token');
      let response;
      
      if (campaignData.id) {
        // Update existing campaign with PUT
        response = await axios.put(`/api/bulk-mailer/campaigns/${campaignData.id}`, requestData, {
          headers: { 'x-auth-token': token }
        });
        
        // Remove the old campaign from the list
        setCampaigns(campaigns.filter(c => c.id !== campaignData.id));
      } else {
        // Create new campaign with POST
        response = await axios.post('/api/bulk-mailer/campaigns', requestData, {
          headers: { 'x-auth-token': token }
        });
      }

      // Process the returned campaign data
      const newCampaign = response.data;

      // Find segment name for display
      const segment = segments.find(s => s.id.toString() === campaignData.segmentId.toString());
      if (segment) {
        newCampaign.segmentName = segment.name;
      }

      // Add the new campaign to the list
      // Always use the subscribed_count for consistency
      const campaignWithDetails = {
        ...newCampaign,
        recipients_count: segmentLeadCount ? segmentLeadCount.subscribed_count : newCampaign.recipients_count || 0,
        segmentName: segment?.name || '',
        // Add a comment to clarify that recipients_count is always subscribed count
        _countNote: "This count represents subscribed recipients only"
      };

      setCampaigns([...campaigns, campaignWithDetails]);
      setShowNewCampaign(false);
      resetForm();

      // Show success message
      setError(null);

    } catch (err) {
      console.error('Error creating campaign:', err);
      setError('Failed to create campaign: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  // Reset form
  const resetForm = () => {
    setCampaignData({
      name: '',
      subject: '',
      templateId: '',
      content: '',
      segmentId: '',
      scheduledDate: '',
      status: 'draft',
      senderId: '',
      tracking_enabled: true
    });
    setSegmentLeadCount(null);
    setError(null);
  };

  // Handle page change for pagination
  const handlePageChange = (newPage) => {
    if (newPage !== pagination.page) {
      // Clear selections when changing pages
      setSelectedCampaigns([]);

      // Update page number
      setPagination({
        ...pagination,
        page: newPage
      });

      // Load data for the new page
      setLoading(true);
    }
  };

  // Handle AI generator input change
  const handleAiInputChange = (e) => {
    const { name, value } = e.target;
    setAiPromptData({
      ...aiPromptData,
      [name]: value
    });
  };

  // Refine email content using AI
  const refineAiEmail = async () => {
    try {
      // Validate refinement instructions
      if (!aiRefinementInstructions) {
        setError('Please enter refinement instructions');
        return;
      }

      // Close the refiner modal first
      setShowAiRefiner(false);

      // Then show the loading animation
      setAiRefining(true);
      setError(null);

      const token = localStorage.getItem('token');

      // Directly call the backend API - backend now handles provider/model/key
      const response = await axios.post(
        '/api/bulk-mailer/refine-email', // Assuming this endpoint exists and handles refinement
        {
          currentHtml: campaignData.content,
          currentSubject: campaignData.subject,
          refinementInstructions: aiRefinementInstructions,
          includeCompanyInfo: includeCompanyInfo
          // REMOVED: provider and model are no longer sent from frontend
        },
        { headers: { 'x-auth-token': token } }
      );

      if (response.data && response.data.html) {
        // Update campaign data with refined content
          setCampaignData({
            ...campaignData,
            content: response.data.html,
            subject: response.data.subject || campaignData.subject
          });

          // Make sure preview is expanded
          setPreviewMode(true);

          // Reset instructions
          setAiRefinementInstructions('');
        } else {
          throw new Error('Refined content is empty or invalid');
        }
    } catch (err) {
      console.error('Error refining email with AI:', err);
      setError('Failed to refine email: ' + (err.response?.data?.message || err.message));

      // In case of error, show the refiner modal again so user can retry
      setShowAiRefiner(true);
    } finally {
      setAiRefining(false);
    }
  };

  // Generate email content using AI
  const generateAiEmail = async () => {
    try {
      // Validate required fields
      if (!aiPromptData.topic || !aiPromptData.audience || !aiPromptData.goal) {
        setError('Please fill in all required AI generator fields.');
        return;
      }

      // Close the generator modal first
      setShowAiGenerator(false);

      // Then show the loading animation
      setAiGenerating(true);
      setError(null);

      const token = localStorage.getItem('token');

      // Directly call the backend API - backend now handles provider/model/key
      const response = await axios.post(
        '/api/bulk-mailer/generate-email',
        {
          ...aiPromptData,
          includeCompanyInfo: includeCompanyInfo
          // REMOVED: provider and model are no longer sent from frontend
        },
        { headers: { 'x-auth-token': token } }
      );

      if (response.data && response.data.html) {
        // Update campaign data with generated content
          setCampaignData({
            ...campaignData,
            content: response.data.html,
            subject: response.data.subject || campaignData.subject
          });

          // Auto-expand the preview after generating content
          setPreviewMode(true);

          // Hide the loading animation
          setAiGenerating(false);

          // Reset AI prompt data
          setAiPromptData({
            topic: '',
            audience: '',
            goal: '',
            tone: 'professional',
            language: 'english',
            additionalInfo: ''
          });
        } else {
          throw new Error('Generated content is empty or invalid');
        }
    } catch (err) {
      console.error('Error generating email with AI:', err);
      setError('Failed to generate email: ' + (err.response?.data?.message || err.message));

      // In case of error, show the generator modal again so user can retry
      setShowAiGenerator(true);
    } finally {
      setAiGenerating(false);
    }
  };

  // Handler for sending a campaign
  const handleSendCampaign = async (campaign) => {
    try {
      // CRITICAL: Validate segment/recipients before allowing send
      if (!campaign.segment_id && !campaign.segmentId && (!campaign.recipients_count || campaign.recipients_count === 0)) {
        setError('Cannot send campaign without recipients. Please select a segment or add recipients to the campaign.');
        return;
      }

      // CRITICAL: Validate sender profile before sending
      if (!campaign.sender_id && !campaign.senderId) {
        setError('Cannot send campaign without a sender profile. Please edit the campaign and select a sender profile.');
        return;
      }

      const recipientCount = campaign.recipients_count || 0;
      if (recipientCount === 0) {
        setError('Cannot send campaign to 0 recipients. Please check the segment configuration or add recipients.');
        return;
      }

      if (!window.confirm(`Are you sure you want to send the campaign "${campaign.name}" to ${recipientCount} recipients? Note: Only subscribed leads will receive the email.`)) {
        return;
      }

      setError(null);
      setLoading(true);

      // Get token for API authentication
      const token = localStorage.getItem('token');

      // Update the UI optimistically
      setCampaigns(prev => prev.map(c => {
        if (c.id === campaign.id) {
          return {
            ...c,
            status: 'sending',
            sentDate: new Date().toISOString().split('T')[0]
          };
        }
        return c;
      }));

      // Make a real API call to update the campaign status to 'sending'
      await axios.put(`/api/bulk-mailer/campaigns/${campaign.id}`,
        { status: 'sending' },
        { headers: { 'x-auth-token': token } }
      );

      // Fetch the updated campaign to get real-time status
      const response = await axios.get(`/api/bulk-mailer/campaigns/${campaign.id}`, {
        headers: { 'x-auth-token': token }
      });

      // Update the campaign in the UI with the latest data from the server
      setCampaigns(prev => prev.map(c => {
        if (c.id === campaign.id) {
          // Create a merged object with updated data from the server
          const updatedCampaign = {
            ...c,
            ...response.data,
            // Keep the segmentName if it exists
            segmentName: c.segmentName || response.data.segmentName
          };
          return updatedCampaign;
        }
        return c;
      }));

    } catch (err) {
      console.error('Error sending campaign:', err);
      setError('Failed to send campaign: ' + (err.response?.data?.message || err.message));

      // If there was an error, revert the UI back to draft status
      setCampaigns(prev => prev.map(c => {
        if (c.id === campaign.id) {
          return {
            ...c,
            status: 'draft'
          };
        }
        return c;
      }));
    } finally {
      setLoading(false);
    }
  };

  // Handler for viewing or editing a campaign
  const handleViewEditCampaign = async (campaign) => {
    try {
      setLoading(true);
      setError(null);

      // Get token for API authentication
      const token = localStorage.getItem('token');

      // Fetch the complete campaign data from the server
      const response = await axios.get(`/api/bulk-mailer/campaigns/${campaign.id}`, {
        headers: { 'x-auth-token': token }
      });

      let campaignDetails = response.data;

      // Fetch tracking analytics if the campaign is sent or completed
      if (['sent', 'completed', 'completed_with_errors'].includes(campaignDetails.status)) {
        try {
          const analyticsResponse = await axios.get(`/api/track/stats/${campaign.id}`, {
            headers: { 'x-auth-token': token }
          });

          // Add analytics data to campaign details
          campaignDetails = {
            ...campaignDetails,
            analytics: analyticsResponse.data
          };
        } catch (analyticsError) {
          console.error('Error fetching campaign analytics:', analyticsError);
          // Continue without analytics data
        }
      }

      // If it's a draft, populate the form for editing
      if (campaign.status === 'draft') {
        setShowNewCampaign(true);

        // Set campaign data for editing
        setCampaignData({
          id: campaignDetails.id, // Keep track of the ID for updates
          name: campaignDetails.name,
          subject: campaignDetails.subject,
          content: campaignDetails.content || '',
          segmentId: campaignDetails.segment_id?.toString() || '',
          scheduledDate: campaignDetails.scheduled_date || '',
          status: campaignDetails.status,
          senderId: campaignDetails.sender_id || '',
          tracking_enabled: campaignDetails.tracking_enabled !== false // Default to true if not specified
        });
      } else {
        // For non-draft campaigns, show them in a view-only modal
        setViewCampaignData(campaignDetails);
        setShowViewCampaign(true);
      }
    } catch (err) {
      console.error('Error fetching campaign details:', err);
      setError('Failed to load campaign details: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  // Handler for rescheduling a campaign
  const handleRescheduleCampaign = async (campaign) => {
    try {
      // For now, use a simple prompt - in a real app, we'd use a date picker component
      const newDate = prompt('Enter new scheduled date (YYYY-MM-DD HH:MM):',
        campaign.scheduledDate || new Date().toISOString().slice(0, 16).replace('T', ' '));

      if (!newDate) return; // User cancelled

      setLoading(true);
      setError(null);

      // Get token for API authentication
      const token = localStorage.getItem('token');

      // Update the campaign with the new scheduled date
      await axios.put(`/api/bulk-mailer/campaigns/${campaign.id}`,
        {
          scheduledDate: newDate,
          status: 'scheduled' // Ensure the status is set to scheduled
        },
        { headers: { 'x-auth-token': token } }
      );

      // Update the campaign in the UI
      setCampaigns(prev => prev.map(c => {
        if (c.id === campaign.id) {
          return {
            ...c,
            scheduledDate: newDate,
            status: 'scheduled'
          };
        }
        return c;
      }));

    } catch (err) {
      console.error('Error rescheduling campaign:', err);
      setError('Failed to reschedule campaign: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  // Handler for duplicating a campaign
  const handleDuplicateCampaign = async (campaign) => {
    try {
      setLoading(true);
      setError(null);

      // Get token for API authentication
      const token = localStorage.getItem('token');

      // First, fetch the complete campaign data to duplicate
      const response = await axios.get(`/api/bulk-mailer/campaigns/${campaign.id}`, {
        headers: { 'x-auth-token': token }
      });

      const campaignToDuplicate = response.data;

      // Create a new campaign based on the existing one
      const newCampaignData = {
        name: `${campaignToDuplicate.name} (Copy)`,
        subject: campaignToDuplicate.subject,
        content: campaignToDuplicate.content,
        text: campaignToDuplicate.text,
        status: 'draft', // Always start as draft
        segmentId: campaignToDuplicate.segment_id
      };

      // Create the duplicate campaign via API
      const createResponse = await axios.post('/api/bulk-mailer/campaigns',
        newCampaignData,
        { headers: { 'x-auth-token': token } }
      );

      const newCampaign = createResponse.data;

      // Add segment name for display if applicable
      if (newCampaign.segment_id) {
        const segment = segments.find(s => s.id.toString() === newCampaign.segment_id.toString());
        if (segment) {
          newCampaign.segmentName = segment.name;
        }
      }

      // Add the new campaign to the list
      setCampaigns([...campaigns, newCampaign]);

    } catch (err) {
      console.error('Error duplicating campaign:', err);
      setError('Failed to duplicate campaign: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  // Handler for deleting a campaign
  const handleDeleteCampaign = async (campaign, silent = false) => {
    try {
      // Confirm before deleting if not in silent mode
      if (!silent && !window.confirm(`Are you sure you want to delete the campaign "${campaign.name}"? This action cannot be undone.`)) {
        return;
      }

      if (!silent) {
        setLoading(true);
      }
      setError(null);

      // Get token for API authentication
      const token = localStorage.getItem('token');

      // Call the delete API endpoint
      await axios.delete(`/api/bulk-mailer/campaigns/${campaign.id}`, {
        headers: { 'x-auth-token': token }
      });

      // Update the campaigns list by removing the deleted campaign
      setCampaigns(prev => prev.filter(c => c.id !== campaign.id));

      // If this campaign was selected, remove it from selections
      if (selectedCampaigns.includes(campaign.id)) {
        setSelectedCampaigns(prev => prev.filter(id => id !== campaign.id));
      }

      // Show success message only if not silent
      if (!silent) {
        // Could add a success notification here if you have a notification system
      }

      return true; // Return success for Promise.all in bulk operations
    } catch (err) {
      console.error('Error deleting campaign:', err);

      // Only show error UI if not silent
      if (!silent) {
        setError('Failed to delete campaign: ' + (err.response?.data?.message || err.message));
      }

      return false; // Return failure for Promise.all
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  };

  // Campaign status badge
  const CampaignStatusBadge = ({ status }) => {
    const statusClasses = {
      draft: 'bg-gray-200 text-gray-800',
      scheduled: 'bg-blue-200 text-blue-800',
      sending: 'bg-yellow-200 text-yellow-800',
      sent: 'bg-green-200 text-green-800',
      completed: 'bg-green-200 text-green-800',
      completed_with_errors: 'bg-yellow-200 text-yellow-800',
      failed: 'bg-red-200 text-red-800'
    };

    // Format status text (capitalize words and replace underscores with spaces)
    const formatStatus = (status) => {
      return status
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    };

    return (
      <span className={`px-2 py-1 rounded text-xs font-medium ${statusClasses[status] || statusClasses.draft}`}>
        {formatStatus(status)}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="p-4">
        <h3 className="text-lg font-semibold mb-4">Email Marketing</h3>
        <div className="bg-white rounded-lg shadow p-6 text-center">
          <p>Loading email marketing data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4">
      {/* Bulk Actions Bar - only shown when campaigns are selected */}
      {selectedCampaigns.length > 0 ? (
        <div className="bg-white rounded-lg shadow-md overflow-hidden mb-6">
          <div className="bg-blue-50 border-b border-blue-200 p-2 flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <span className="text-sm font-medium text-blue-700">
                {selectedCampaigns.length} campaign{selectedCampaigns.length !== 1 ? 's' : ''} selected
              </span>
              <Button
                onClick={() => setSelectedCampaigns([])}
                variant="link"
                className="text-xs"
              >
                Clear selection
              </Button>
            </div>
            <div className="flex space-x-2">
              <Button
                variant="outline"
                className="text-sm border-red-200 text-red-600 hover:bg-red-50"
                onClick={() => {
                  // Prepare campaigns for deletion
                  const deletableCampaigns = campaigns.filter(
                    c => selectedCampaigns.includes(c.id) && c.status !== 'sending'
                  );

                  if (deletableCampaigns.length > 0) {
                    // Set the campaigns to delete and show the modal
                    setCampaignsToDelete(deletableCampaigns);
                    setShowDeleteModal(true);
                  } else {
                    // Show message if no deletable campaigns
                    alert('No campaigns can be deleted. Campaigns that are currently sending cannot be deleted.');
                  }
                }}
              >
                Delete Selected
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-semibold">Email Marketing</h3>
          <Button
            onClick={() => setShowNewCampaign(!showNewCampaign)}
            variant="primary"
            className="px-4 py-2"
          >
            {showNewCampaign ? 'Cancel' : '+ New Campaign'}
          </Button>
        </div>
      )}

      {error && (
        <div className="mb-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {/* New Campaign Form */}
      {showNewCampaign && (
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h4 className="text-lg font-medium mb-4">New Email Campaign</h4>

          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Campaign Name*
                </label>
                <input
                  type="text"
                  name="name"
                  value={campaignData.name}
                  onChange={handleInputChange}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Template
                </label>
                <select
                  name="templateId"
                  value={campaignData.templateId}
                  onChange={handleInputChange}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                >
                  <option value="">Select a template...</option>
                  {templates.map(template => (
                    <option key={template.id} value={template.id}>
                      {template.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Sender Profile
                </label>
                <select
                  name="senderId"
                  value={campaignData.senderId}
                  onChange={handleInputChange}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                >
                  <option value="">Select a sender profile...</option>
                  {senderProfiles.map(profile => (
                    <option 
                      key={profile.id} 
                      value={profile.id}
                      disabled={profile.disabled}
                      style={profile.disabled ? { color: '#999', fontStyle: 'italic' } : {}}
                    >
                      {profile.name}
                      {profile.is_default && ' (Default)'}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Subject Line*
                </label>
                <input
                  type="text"
                  name="subject"
                  value={campaignData.subject}
                  onChange={handleInputChange}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                  required
                />
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email Content*
              </label>
              <div className="flex mb-2">
                <Button
                  type="button"
                  onClick={() => {
                    // REMOVED: Obsolete check for company-specific AI config
                    // if (!aiConfigured) {
                    //    setError("AI is not configured. Please add API keys in Settings > Amazon SES / AI.");
                    //    return;
                    // }
                    setShowAiGenerator(true);
                  }}
                  variant="primary"
                  className="!bg-indigo-600 hover:!bg-indigo-700 !text-sm px-3 py-1"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z" />
                  </svg>
                  Generate with AI
                </Button>
              </div>

              <textarea
                name="content"
                value={campaignData.content}
                onChange={handleInputChange}
                className="w-full border border-gray-300 rounded px-3 py-2 h-64"
                required
              />
              <div className="text-xs text-gray-500 mt-1">
                Use {'{{lead_name}}'}, {'{{lead_email}}'}, {'{{country}}'} as variables that will be replaced with recipient data.
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Recipients (Select Segment) {campaignData.status === 'draft' && <span className="text-xs font-normal text-gray-500">- Optional for drafts</span>}
                </label>
                <div className="flex items-center space-x-2">
                  <select
                    name="segmentId"
                    value={campaignData.segmentId || ''}
                    onChange={handleInputChange}
                    className="w-full border border-gray-300 rounded px-3 py-2"
                  >
                    <option value="">Select a segment...</option>
                    {segments.map(segment => (
                      <option key={segment.id} value={segment.id}>
                        {segment.name} ({segment.subscribed_count || 0} subscribed)
                      </option>
                    ))}
                  </select>
                  <Button
                    type="button"
                    onClick={() => setShowSegmentsModal(true)}
                    variant="outline"
                    className="bg-gray-100 hover:bg-gray-200 px-3 py-2"
                  >
                    Browse
                  </Button>
                </div>

                {/* Display segment recipient count */}
                {campaignData.segmentId && (
                  <div className="mt-2 text-sm text-gray-600">
                    {segmentLeadCount === null ? (
                      <span>Loading recipient count...</span>
                    ) : (
                      <div>
                        <span><strong className="text-green-600">{segmentLeadCount.subscribed_count}</strong> subscribed recipient(s) in this segment</span>
                        <span className="ml-1 text-gray-500">
                          (out of {segmentLeadCount.count} total)
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Campaign Status
                </label>
                <select
                  name="status"
                  value={campaignData.status}
                  onChange={handleInputChange}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                >
                  <option value="draft">Save as Draft</option>
                  <option value="scheduled">Schedule for Later</option>
                </select>
              </div>
            </div>

            <div className="mb-6">
              <div className="flex items-center">
                <input
                  id="tracking-enabled"
                  type="checkbox"
                  checked={campaignData.tracking_enabled}
                  onChange={(e) => setCampaignData({
                    ...campaignData,
                    tracking_enabled: e.target.checked
                  })}
                  className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                />
                <label htmlFor="tracking-enabled" className="ml-2 block text-sm text-gray-700">
                  Enable email tracking
                </label>
              </div>
              <p className="text-xs text-gray-500 mt-1 ml-6">
                When enabled, the system will track email opens and link clicks. This helps you measure campaign performance and create targeted follow-up segments.
              </p>
            </div>

            {campaignData.status === 'scheduled' && (
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Schedule Date & Time
                </label>
                <input
                  type="datetime-local"
                  name="scheduledDate"
                  value={campaignData.scheduledDate}
                  onChange={handleInputChange}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                  required
                />
              </div>
            )}


            {/* Preview Button and Device Toggle */}
            <div className="flex justify-between mb-6">
              <Button
                type="button"
                onClick={() => setPreviewMode(!previewMode)}
                variant="outline"
                className="bg-gray-100 hover:bg-gray-200 px-4 py-2"
              >
                {previewMode ? 'Hide Preview' : 'Show Preview'}
              </Button>
              
              {previewMode && (
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-gray-600">Preview device:</span>
                  <div className="flex rounded-md border border-gray-300 overflow-hidden">
                    <button 
                      className={`px-3 py-1 text-sm font-medium ${previewDevice === 'desktop' 
                        ? 'bg-blue-100 text-blue-700' 
                        : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                      onClick={() => setPreviewDevice('desktop')}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 inline-block mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      Desktop
                    </button>
                    <button 
                      className={`px-3 py-1 text-sm font-medium ${previewDevice === 'mobile' 
                        ? 'bg-blue-100 text-blue-700' 
                        : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                      onClick={() => setPreviewDevice('mobile')}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 inline-block mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                      </svg>
                      Mobile
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Email Preview */}
            {previewMode && (
              <div className="mb-6 border border-gray-300 rounded-lg p-4 bg-gray-50">
                <div className="flex justify-between items-center mb-2">
                  <h5 className="font-medium">
                    Email Preview 
                    {previewDevice === 'mobile' && <span className="text-xs text-gray-500 ml-2">(Mobile View)</span>}
                    {previewDevice === 'desktop' && <span className="text-xs text-gray-500 ml-2">(Desktop View)</span>}
                  </h5>
                  {campaignData.content && campaignData.content.includes('<') && (
                    <Button
                      onClick={() => {
                        // REMOVED: Obsolete check for company-specific AI config
                        // if (!aiConfigured) {
                        //    setError("AI is not configured. Please add API keys in Settings > Amazon SES / AI.");
                        //    return;
                        // }
                        setShowAiRefiner(true);
                      }}
                      variant="primary"
                      className="!bg-indigo-600 hover:!bg-indigo-700 !text-sm px-3 py-1"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z" />
                      </svg>
                      Refine with AI
                    </Button>
                  )}
                </div>
                
                <div className={`mx-auto transition-all duration-300 ${
                  previewDevice === 'mobile' 
                    ? 'max-w-[430px] border-[10px] border-gray-800 rounded-[36px] shadow-lg' 
                    : 'border border-gray-200 rounded-lg'
                }`}>
                  {previewDevice === 'mobile' && (
                    <div className="bg-black h-6 flex justify-center items-center rounded-t-lg">
                      <div className="w-20 h-2 bg-gray-700 rounded-full"></div>
                    </div>
                  )}
                  
                  <div className={`bg-white ${previewDevice === 'mobile' ? 'h-[750px] overflow-y-auto rounded-b-lg text-sm' : 'p-4'}`}>
                    <div className={previewDevice === 'mobile' ? 'p-3 sticky top-0 bg-white border-b z-10' : 'mb-2'}>
                      <strong>Subject:</strong> {campaignData.subject.replace(/{{lead_name}}/g, 'John Doe').replace(/{{country}}/g, 'USA')}
                    </div>
                    <div className={previewDevice === 'mobile' ? 'p-3 pt-2' : 'border-t pt-2'}>
                      <div className={previewDevice === 'mobile' ? 'text-sm' : ''} 
                        dangerouslySetInnerHTML={{ __html: getPreviewContent(campaignData.content, { name: 'John Doe', email: 'john@example.com', country: 'USA' }) }} />
                    </div>
                  </div>
                  
                  {previewDevice === 'mobile' && (
                    <div className="bg-black h-6 flex justify-center items-center">
                      <div className="w-8 h-2 bg-gray-700 rounded-full"></div>
                    </div>
                  )}
                </div>
                
                {previewDevice === 'mobile' && (
                  <div className="mt-4 text-xs text-gray-500 text-center">
                    Mobile preview shows how your email might appear on smartphones (430px width). Content is scrollable as on real devices.
                  </div>
                )}
              </div>
            )}

            <div className="flex justify-end space-x-3">
              <Button
                type="button"
                onClick={() => {
                  resetForm();
                  setShowNewCampaign(false);
                }}
                variant="outline"
                className="px-4 py-2"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                className="px-4 py-2"
              >
                Save Campaign
              </Button>
            </div>
          </form>

          {/* Segments selection modal */}
          <SegmentsListModal
            isOpen={showSegmentsModal}
            onClose={() => setShowSegmentsModal(false)}
            onSelectSegment={(segment) => {
              setCampaignData({
                ...campaignData,
                segmentId: segment.id
              });
              fetchSegmentLeadCount(segment.id);
              setShowSegmentsModal(false);
            }}
          />

          {/* AI Email Refiner Modal */}
          {showAiRefiner && campaignData.content && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                <div className="px-6 py-4 border-b">
                  <div className="flex justify-between items-center">
                    <h3 className="text-lg font-medium">Refine Email Content with AI</h3>
                    {/* Removed display of provider/model as it's central */}
                  </div>
                </div>

                <div className="p-6">
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      How would you like to improve this email?
                    </label>
                    <textarea
                      value={aiRefinementInstructions}
                      onChange={(e) => setAiRefinementInstructions(e.target.value)}
                      className="w-full border border-gray-300 rounded px-3 py-2 h-24"
                      placeholder="Example: Make it shorter, use more professional language, add more details about our new feature, etc."
                      required
                    />
                  </div>

                  <div className="bg-gray-50 rounded-lg p-3 mb-4">
                    <div className="text-sm font-medium text-gray-700 mb-2">Common Refinements</div>
                    <div className="flex flex-wrap gap-2">
                      {[
                        "Make it shorter",
                        "Make it longer",
                        "More professional tone",
                        "More friendly tone",
                        "Simplify the language",
                        "Add more details",
                        "More persuasive",
                        "Improve call to action"
                      ].map((suggestion, index) => (
                        <Button
                          key={index}
                          type="button"
                          onClick={() => setAiRefinementInstructions(prev =>
                            prev ? `${prev}\n${suggestion}` : suggestion
                          )}
                          variant="outline"
                          className="!text-xs py-1 px-2 bg-white hover:bg-gray-100"
                        >
                          {suggestion}
                        </Button>
                      ))}
                    </div>
                  </div>

                  <div className="mb-4">
                    <div className="flex items-center">
                      <input
                        id="include-company-info-refine"
                        type="checkbox"
                        checked={includeCompanyInfo}
                        onChange={() => setIncludeCompanyInfo(!includeCompanyInfo)}
                        className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                      />
                      <label htmlFor="include-company-info-refine" className="ml-2 block text-sm text-gray-700">
                        Use company profile
                      </label>
                    </div>
                    <p className="mt-1 text-xs text-gray-500 ml-6">
                      When enabled, the AI will read your company name, description, and logo from settings. Disable this for generic content or when testing new services.
                    </p>
                  </div>

                  {/* Informational message */}
                  <div className="mb-4 p-3 bg-blue-50 border border-blue-100 rounded-lg text-sm text-blue-700">
                    <div className="flex items-start">
                      <svg className="h-5 w-5 text-blue-400 mt-0.5 mr-2 flex-shrink-0" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                      </svg>
                      <div>
                        Your refinements will be applied to the current email content visible in the preview. The AI will maintain the overall structure while making your requested changes.
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 flex justify-end space-x-3">
                    <Button
                      type="button"
                      onClick={() => setShowAiRefiner(false)}
                      variant="outline"
                      className="px-4 py-2"
                      disabled={aiRefining}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="button"
                      onClick={refineAiEmail}
                      variant="primary"
                      className="!bg-indigo-600 hover:!bg-indigo-700 px-4 py-2"
                      disabled={aiRefining || !aiRefinementInstructions}
                      isLoading={aiRefining}
                    >
                      {aiRefining ? (
                        <>
                          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Refining...
                        </>
                      ) : (
                        'Refine Email'
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* AI Email Generator Modal */}
          {showAiGenerator && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                <div className="px-6 py-4 border-b">
                  <div className="flex justify-between items-center">
                    <h3 className="text-lg font-medium">Generate Email Content with AI</h3>
                    {/* Removed display of provider/model as it's central */}
                  </div>
                </div>

                <div className="p-6">
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email Topic/Purpose* <span className="text-xs text-gray-500">(What is this email about?)</span>
                    </label>
                    <input
                      type="text"
                      name="topic"
                      value={aiPromptData.topic}
                      onChange={handleAiInputChange}
                      className="w-full border border-gray-300 rounded px-3 py-2"
                      placeholder="E.g., 'Summer language course promotion' or 'Welcome to our online learning platform'"
                      required
                    />
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Target Audience* <span className="text-xs text-gray-500">(Who will receive this email?)</span>
                    </label>
                    <input
                      type="text"
                      name="audience"
                      value={aiPromptData.audience}
                      onChange={handleAiInputChange}
                      className="w-full border border-gray-300 rounded px-3 py-2"
                      placeholder="E.g., 'Parents of high-school students' or 'Adult language learners'"
                      required
                    />
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email Goal* <span className="text-xs text-gray-500">(What action do you want recipients to take?)</span>
                    </label>
                    <input
                      type="text"
                      name="goal"
                      value={aiPromptData.goal}
                      onChange={handleAiInputChange}
                      className="w-full border border-gray-300 rounded px-3 py-2"
                      placeholder="E.g., 'Sign up for a free trial lesson' or 'Register for the summer course'"
                      required
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Tone <span className="text-xs text-gray-500">(How should the email sound?)</span>
                      </label>
                      <select
                        name="tone"
                        value={aiPromptData.tone}
                        onChange={handleAiInputChange}
                        className="w-full border border-gray-300 rounded px-3 py-2"
                      >
                        <option value="professional">Professional</option>
                        <option value="friendly">Friendly</option>
                        <option value="casual">Casual</option>
                        <option value="formal">Formal</option>
                        <option value="enthusiastic">Enthusiastic</option>
                      </select>
                    </div>

                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Language <span className="text-xs text-gray-500">(Select output language)</span>
                      </label>
                      <select
                        name="language"
                        value={aiPromptData.language}
                        onChange={handleAiInputChange}
                        className="w-full border border-gray-300 rounded px-3 py-2"
                      >
                        <option value="english">English</option>
                        <option value="norwegian">Norwegian</option>
                        <option value="swedish">Swedish</option>
                        <option value="german">German</option>
                      </select>
                    </div>
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Additional Information <span className="text-xs text-gray-500">(Any specific details to include?)</span>
                    </label>
                    <textarea
                      name="additionalInfo"
                      value={aiPromptData.additionalInfo}
                      onChange={handleAiInputChange}
                      className="w-full border border-gray-300 rounded px-3 py-2 h-20"
                      placeholder="E.g., 'Mention 20% discount for early registration' or 'Highlight our qualified teachers'"
                    />
                  </div>

                  <div className="mb-6">
                    <div className="flex items-center">
                      <input
                        id="include-company-info"
                        type="checkbox"
                        checked={includeCompanyInfo}
                        onChange={() => setIncludeCompanyInfo(!includeCompanyInfo)}
                        className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                      />
                      <label htmlFor="include-company-info" className="ml-2 block text-sm text-gray-700">
                        Use company profile
                      </label>
                    </div>
                    <p className="mt-1 text-xs text-gray-500 ml-6">
                      When enabled, the AI will read your company name, description, and logo from settings.
                      Disable this for generic content or when testing new services.
                    </p>
                  </div>

                  <div className="mt-4 flex justify-end space-x-3">
                    <Button
                      type="button"
                      onClick={() => setShowAiGenerator(false)}
                      variant="outline"
                      className="px-4 py-2"
                      disabled={aiGenerating}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="button"
                      onClick={generateAiEmail}
                      variant="primary"
                      className="!bg-indigo-600 hover:!bg-indigo-700 px-4 py-2"
                      disabled={aiGenerating}
                      isLoading={aiGenerating}
                    >
                      {aiGenerating ? (
                        <>
                          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Generating...
                        </>
                      ) : (
                        'Generate Email'
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Campaign List */}
      <div className="bg-white rounded-lg shadow">
        <SharedTable
          data={campaigns}
          columns={[
            { id: 'name', label: 'Campaign Name', sortable: true, visible: true, width: 200 },
            { id: 'subject', label: 'Subject', sortable: true, visible: true, width: 300 },
            { id: 'status', label: 'Status', sortable: true, visible: true, width: 120 },
            { id: 'recipients', label: 'Recipients', sortable: true, visible: true, width: 150 },
            { id: 'date', label: 'Date', sortable: true, visible: true, width: 170 },
            { id: 'performance', label: 'Performance', sortable: false, visible: true, width: 180 },
            { id: 'actions', label: 'Actions', sortable: false, visible: true, width: 200 }
          ]}
          sortBy="date"
          sortOrder="desc"
          onSort={(columnId) => {
            // Implement sorting logic if needed
          }}
          selectedItems={selectedCampaigns}
          onSelectItem={(campaignId) => {
            setSelectedCampaigns(prev =>
              prev.includes(campaignId)
                ? prev.filter(id => id !== campaignId)
                : [...prev, campaignId]
            );
          }}
          selectAll={selectedCampaigns.length === campaigns.length && campaigns.length > 0}
          onSelectAll={() => {
            if (selectedCampaigns.length === campaigns.length) {
              setSelectedCampaigns([]);
            } else {
              setSelectedCampaigns(campaigns.map(campaign => campaign.id));
            }
          }}
          loading={loading}
          emptyMessage="No email campaigns yet. Create your first campaign!"
          renderCell={(campaign, columnId) => {
            switch (columnId) {
              case 'name':
                return <div className="font-medium text-gray-900">{campaign.name}</div>;
              case 'subject':
                return <div className="text-sm text-gray-900 truncate max-w-xs">{campaign.subject}</div>;
              case 'status':
                return <CampaignStatusBadge status={campaign.status} />;
              case 'recipients':
                return (
                  <div className="text-sm text-gray-500">
                    {campaign.status === 'draft' && !campaign.segment_id && !campaign.segmentId ? (
                      <span className="italic">No segment selected</span>
                    ) : (
                      <>
                        {campaign.recipients_count || 0}
                        <span className="ml-1 text-xs text-gray-500">
                          (subscribed)
                        </span>
                        {(campaign.segmentName || campaign.segment_name) && (
                          <span className="ml-1 text-xs text-blue-500">
                            ({campaign.segmentName || campaign.segment_name})
                          </span>
                        )}
                      </>
                    )}
                  </div>
                );
              case 'date':
                return (
                  <div className="text-sm text-gray-500">
                    {campaign.sent_at || campaign.sending_completed_at || campaign.sentDate ||
                     campaign.scheduled_date || campaign.scheduledDate ||
                     campaign.updated_at || campaign.lastEdited}
                  </div>
                );
              case 'performance':
                if (campaign.status === 'sent' || campaign.status === 'completed') {
                  return (
                    <div className="text-sm">
                      {campaign.stats ? (
                        <>
                          <span className="text-blue-600">
                            {Math.round((campaign.stats.opened / (campaign.recipients_count || 1)) * 100)}% Open
                          </span>
                          {' • '}
                          <span className="text-green-600">
                            {Math.round((campaign.stats.clicked / (campaign.recipients_count || 1)) * 100)}% Click
                          </span>
                        </>
                      ) : (
                        <>
                          <span className="text-blue-600">
                            {Math.round(((campaign.opens || 0) / (campaign.recipients_count || 1)) * 100)}% Open
                          </span>
                          {' • '}
                          <span className="text-green-600">
                            {Math.round(((campaign.clicks || 0) / (campaign.recipients_count || 1)) * 100)}% Click
                          </span>
                        </>
                      )}
                    </div>
                  );
                } else {
                  return <div className="text-sm text-gray-500">-</div>;
                }
              case 'actions':
                return (
                  <div className="flex justify-end space-x-2">
                    <Button
                      variant="link"
                      className="text-blue-600 hover:text-blue-900"
                      onClick={() => handleViewEditCampaign(campaign)}
                    >
                      {campaign.status === 'draft' ? 'Edit' : 'View'}
                    </Button>
                    {campaign.status === 'draft' && (
                      <>
                        <Button
                          variant="link"
                          className="text-green-600 hover:text-green-900"
                          onClick={() => handleSendCampaign(campaign)}
                        >
                          Send
                        </Button>
                      </>
                    )}
                    {campaign.status !== 'sending' && (
                      <Button
                        variant="link"
                        className="text-red-600 hover:text-red-900"
                        onClick={() => {
                          setCampaignsToDelete([campaign]);
                          setShowDeleteModal(true);
                        }}
                      >
                        Delete
                      </Button>
                    )}
                    {campaign.status === 'scheduled' && (
                      <Button
                        variant="link"
                        className="text-yellow-600 hover:text-yellow-900"
                        onClick={() => handleRescheduleCampaign(campaign)}
                      >
                        Reschedule
                      </Button>
                    )}
                    {(campaign.status === 'sent' || campaign.status === 'completed') && (
                      <Button
                        variant="link"
                        className="text-indigo-600 hover:text-indigo-900"
                        onClick={() => handleDuplicateCampaign(campaign)}
                      >
                        Duplicate
                      </Button>
                    )}
                  </div>
                );
              default:
                return null;
            }
          }}
          resizable={true}
          tableHeight="calc(100vh - 380px)"
          pagination={pagination}
          onPageChange={handlePageChange}
          onLimitChange={handleLimitChange} // Pass the new handler
        />
      </div>

      {/* Campaign View Modal */}
      {showViewCampaign && viewCampaignData && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => {
            setShowViewCampaign(false);
            setViewCampaignData(null);
          }}
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              setShowViewCampaign(false);
              setViewCampaignData(null);
            }
          }}
          ref={(el) => el && el.focus()}
        >
          <div 
            className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()} // Prevent closing when clicking on the modal itself
          >
            <div className="px-6 py-4 border-b flex justify-between items-center">
              <h3 className="text-lg font-medium">{viewCampaignData.name}</h3>
              <div className="flex items-center space-x-3">
                <CampaignStatusBadge status={viewCampaignData.status} />
                <button
                  onClick={() => {
                    setShowViewCampaign(false);
                    setViewCampaignData(null);
                  }}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            <div className="p-6">
              {/* Campaign Details */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <h4 className="text-sm font-medium text-gray-700">Campaign Details</h4>
                    
                    {/* Add device toggle in view modal as well */}
                    <div className="flex items-center space-x-2">
                      <span className="text-xs text-gray-600">Preview:</span>
                      <div className="flex rounded-md border border-gray-300 overflow-hidden">
                        <button 
                          className={`px-2 py-0.5 text-xs font-medium ${previewDevice === 'desktop' 
                            ? 'bg-blue-100 text-blue-700' 
                            : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                          onClick={() => setPreviewDevice('desktop')}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 inline-block mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                          </svg>
                          Desktop
                        </button>
                        <button 
                          className={`px-2 py-0.5 text-xs font-medium ${previewDevice === 'mobile' 
                            ? 'bg-blue-100 text-blue-700' 
                            : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                          onClick={() => setPreviewDevice('mobile')}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 inline-block mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                          </svg>
                          Mobile
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="bg-gray-50 p-4 rounded border border-gray-200">
                    <div className="grid grid-cols-3 gap-2 text-sm">
                      <div className="text-gray-500">ID:</div>
                      <div className="col-span-2 font-mono">{viewCampaignData.id}</div>

                      <div className="text-gray-500">Created:</div>
                      <div className="col-span-2">
                        {new Date(viewCampaignData.created_at).toLocaleString()}
                      </div>

                      {viewCampaignData.sending_started_at && (
                        <>
                          <div className="text-gray-500">Sent:</div>
                          <div className="col-span-2">
                            {new Date(viewCampaignData.sending_started_at).toLocaleString()}
                          </div>
                        </>
                      )}

                      <div className="text-gray-500">Subject:</div>
                      <div className="col-span-2 font-medium">{viewCampaignData.subject}</div>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Performance Metrics</h4>
                  <div className="bg-gray-50 p-4 rounded border border-gray-200">
                    <div className="grid grid-cols-1 gap-3">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">Recipients:</span>
                        <span className="text-sm font-medium">
                          {viewCampaignData.recipients_count || 0}
                          {(viewCampaignData.status === 'sending' ||
                            viewCampaignData.status === 'sent' ||
                            viewCampaignData.status === 'completed' ||
                            viewCampaignData.status === 'completed_with_errors') && (
                            <span className="ml-1 text-xs text-gray-500">(subscribed only)</span>
                          )}
                        </span>
                      </div>

                      {viewCampaignData.segment_name && (
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-600">Segment:</span>
                          <span className="text-sm font-medium">{viewCampaignData.segment_name}</span>
                        </div>
                      )}

                      {/* Campaign analytics data */}
                      {viewCampaignData.analytics && (
                        <>
                          {/* Tracking Metrics */}
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600">Sent:</span>
                            <span className="text-sm font-medium">
                              {viewCampaignData.analytics.stats.sent.unique || viewCampaignData.sent_count || 0}
                              <span className="text-xs ml-1 text-gray-500">
                                recipients
                              </span>
                            </span>
                          </div>

                          <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600">
                              <span className="flex items-center">
                                <span className="inline-block w-2 h-2 rounded-full bg-gray-500 mr-1"></span>
                                Delivered:
                              </span>
                            </span>
                            <span className="text-sm font-medium">
                              {viewCampaignData.delivered_count || 0}
                              <span className="text-xs ml-1 text-gray-500">
                                ({Math.round(((viewCampaignData.delivered_count || 0) /
                                (viewCampaignData.recipients_count || 1)) * 100)}%)
                              </span>
                            </span>
                          </div>

                          <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600">
                              <span className="flex items-center">
                                <span className="inline-block w-2 h-2 rounded-full bg-red-500 mr-1"></span>
                                Bounced:
                              </span>
                            </span>
                            <span className="text-sm font-medium text-red-600">
                              {viewCampaignData.bounced_count || 0}
                              <span className="text-xs ml-1 text-red-500">
                                ({Math.round(((viewCampaignData.bounced_count || 0) /
                                (viewCampaignData.recipients_count || 1)) * 100)}%)
                              </span>
                            </span>
                          </div>

                          <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600">
                              <span className="flex items-center">
                                <span className="inline-block w-2 h-2 rounded-full bg-yellow-500 mr-1"></span>
                                Complaints:
                              </span>
                            </span>
                            <span className="text-sm font-medium text-yellow-600">
                              {viewCampaignData.complaint_count || 0}
                              <span className="text-xs ml-1 text-yellow-500">
                                ({Math.round(((viewCampaignData.complaint_count || 0) /
                                (viewCampaignData.recipients_count || 1)) * 100)}%)
                              </span>
                            </span>
                          </div>
                          
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600">
                              <span className="flex items-center">
                                <span className="inline-block w-2 h-2 rounded-full bg-purple-500 mr-1"></span>
                                Unsubscribed:
                              </span>
                            </span>
                            <span className="text-sm font-medium text-purple-600">
                              {viewCampaignData.unsubscribe_count || 0}
                              <span className="text-xs ml-1 text-purple-500">
                                ({Math.round(((viewCampaignData.unsubscribe_count || 0) /
                                (viewCampaignData.recipients_count || 1)) * 100)}%)
                              </span>
                            </span>
                          </div>

                          <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600">
                              <span className="flex items-center">
                                <span className="inline-block w-2 h-2 rounded-full bg-blue-500 mr-1"></span>
                                Opened:
                              </span>
                            </span>
                            <span className="text-sm font-medium text-blue-600">
                              {viewCampaignData.analytics.stats.open.unique || 0}
                              <span className="text-xs ml-1 text-blue-500">
                                ({viewCampaignData.analytics.rates.openRate}%)
                              </span>
                              <button
                                onClick={() => {
                                  // Open a segment creation modal for opened recipients
                                  const segmentName = `Opened: ${viewCampaignData.name.substring(0, 20)}`;
                                  if (window.confirm(`Create a segment from recipients who opened "${viewCampaignData.name}"?`)) {
                                    // Call the API to create a segment
                                    const token = localStorage.getItem('token');
                                    axios.post('/api/track/segment', {
                                      campaignId: viewCampaignData.id,
                                      eventType: 'open',
                                      segmentName
                                    }, {
                                      headers: { 'x-auth-token': token }
                                    })
                                    .then(response => {
                                      alert(`Segment "${segmentName}" created with ${response.data.member_count} recipients`);
                                    })
                                    .catch(error => {
                                      console.error('Error creating segment:', error);
                                      alert('Failed to create segment. Please try again.');
                                    });
                                  }
                                }}
                                className="ml-2 text-xs text-blue-600 underline cursor-pointer"
                                title="Create segment from opens"
                              >
                                Create segment
                              </button>
                            </span>
                          </div>

                          <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600">
                              <span className="flex items-center">
                                <span className="inline-block w-2 h-2 rounded-full bg-green-500 mr-1"></span>
                                Clicked:
                              </span>
                            </span>
                            <span className="text-sm font-medium text-green-600">
                              {viewCampaignData.analytics.stats.click.unique || 0}
                              <span className="text-xs ml-1 text-green-500">
                                ({viewCampaignData.analytics.rates.clickRate}%)
                              </span>
                              <button
                                onClick={() => {
                                  // Open a segment creation modal for clicked recipients
                                  const segmentName = `Clicked: ${viewCampaignData.name.substring(0, 20)}`;
                                  if (window.confirm(`Create a segment from recipients who clicked links in "${viewCampaignData.name}"?`)) {
                                    // Call the API to create a segment
                                    const token = localStorage.getItem('token');
                                    axios.post('/api/track/segment', {
                                      campaignId: viewCampaignData.id,
                                      eventType: 'click',
                                      segmentName
                                    }, {
                                      headers: { 'x-auth-token': token }
                                    })
                                    .then(response => {
                                      alert(`Segment "${segmentName}" created with ${response.data.member_count} recipients`);
                                    })
                                    .catch(error => {
                                      console.error('Error creating segment:', error);
                                      alert('Failed to create segment. Please try again.');
                                    });
                                  }
                                }}
                                className="ml-2 text-xs text-green-600 underline cursor-pointer"
                                title="Create segment from clicks"
                              >
                                Create segment
                              </button>
                            </span>
                          </div>

                          {/* Popular links */}
                          {viewCampaignData.analytics.topLinks && viewCampaignData.analytics.topLinks.length > 0 && (
                            <div className="mt-2 border-t pt-2">
                              <span className="text-sm font-medium text-gray-700">Popular Links:</span>
                              <ul className="mt-1 space-y-1">
                                {viewCampaignData.analytics.topLinks.map((link, index) => (
                                  <li key={index} className="text-xs flex justify-between">
                                    <span className="truncate max-w-[150px]" title={link.url_clicked}>
                                      {link.url_clicked}
                                    </span>
                                    <span className="text-green-600 font-medium">{link.click_count} clicks</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </>
                      )}

                      {/* Legacy stats display for backward compatibility */}
                      {!viewCampaignData.analytics && viewCampaignData.stats && (
                        <>
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600">Delivered:</span>
                            <span className="text-sm font-medium">
                              {viewCampaignData.stats.sent || 0}
                              <span className="text-xs ml-1 text-gray-500">
                                ({Math.round((viewCampaignData.stats.sent / (viewCampaignData.recipients_count || 1)) * 100)}%)
                              </span>
                            </span>
                          </div>

                          <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600">Opened:</span>
                            <span className="text-sm font-medium text-blue-600">
                              {viewCampaignData.stats.opened || 0}
                              <span className="text-xs ml-1 text-blue-500">
                                ({Math.round((viewCampaignData.stats.opened / (viewCampaignData.recipients_count || 1)) * 100)}%)
                              </span>
                            </span>
                          </div>

                          <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600">Clicked:</span>
                            <span className="text-sm font-medium text-green-600">
                              {viewCampaignData.stats.clicked || 0}
                              <span className="text-xs ml-1 text-green-500">
                                ({Math.round((viewCampaignData.stats.clicked / (viewCampaignData.recipients_count || 1)) * 100)}%)
                              </span>
                            </span>
                          </div>

                          <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600">Bounced:</span>
                            <span className="text-sm font-medium text-red-600">
                              {viewCampaignData.stats.bounced || 0}
                              <span className="text-xs ml-1 text-red-500">
                                ({Math.round((viewCampaignData.stats.bounced / (viewCampaignData.recipients_count || 1)) * 100)}%)
                              </span>
                            </span>
                          </div>
                          
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600">Unsubscribed:</span>
                            <span className="text-sm font-medium text-purple-600">
                              {viewCampaignData.stats.unsubscribed || 0}
                              <span className="text-xs ml-1 text-purple-500">
                                ({Math.round((viewCampaignData.stats.unsubscribed / (viewCampaignData.recipients_count || 1)) * 100)}%)
                              </span>
                            </span>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Email Content Preview */}
              <div className="mt-4">
                <h4 className="text-sm font-medium text-gray-700 mb-2">
                  Email Content 
                  {previewDevice === 'mobile' && <span className="text-xs text-gray-500 ml-2">(Mobile View)</span>}
                  {previewDevice === 'desktop' && <span className="text-xs text-gray-500 ml-2">(Desktop View)</span>}
                </h4>
                
                <div className={`mt-2 mx-auto transition-all duration-300 ${
                  previewDevice === 'mobile' 
                    ? 'max-w-[430px] border-[10px] border-gray-800 rounded-[36px] shadow-lg' 
                    : 'border border-gray-200 rounded bg-white p-6 max-h-[500px] overflow-y-auto'
                }`}>
                  {previewDevice === 'mobile' && (
                    <div className="bg-black h-6 flex justify-center items-center rounded-t-lg">
                      <div className="w-20 h-2 bg-gray-700 rounded-full"></div>
                    </div>
                  )}
                  
                  <div className={`bg-white ${previewDevice === 'mobile' ? 'h-[750px] overflow-y-auto text-sm' : 'p-6'}`}>
                    <div className={`${previewDevice === 'mobile' ? 'p-3 sticky top-0 bg-white border-b z-10 text-base' : 'font-medium mb-4 text-lg'}`}>
                      {viewCampaignData.subject}
                    </div>
                    <div className={previewDevice === 'mobile' ? 'p-3' : ''}>
                      <div
                        className={`prose ${previewDevice === 'mobile' ? 'max-w-none text-sm' : 'max-w-none'}`}
                        dangerouslySetInnerHTML={{ __html: viewCampaignData.content }}
                      />
                    </div>
                  </div>
                  
                  {previewDevice === 'mobile' && (
                    <div className="bg-black h-6 flex justify-center items-center">
                      <div className="w-8 h-2 bg-gray-700 rounded-full"></div>
                    </div>
                  )}
                </div>
                
                {previewDevice === 'mobile' && (
                  <div className="mt-4 text-xs text-gray-500 text-center">
                    Mobile preview shows how your email might appear on modern iPhone screens (430px width). Content is scrollable as on real devices.
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="mt-6 flex justify-end space-x-3">
                <Button
                  type="button"
                  onClick={() => {
                    setShowViewCampaign(false);
                    setViewCampaignData(null);
                  }}
                  variant="outline"
                >
                  Close
                </Button>

                <Button
                  type="button"
                  onClick={() => handleDuplicateCampaign(viewCampaignData)}
                  variant="primary"
                >
                  Duplicate Campaign
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={() => {
          // Execute bulk delete
          Promise.all(
            campaignsToDelete.map(campaign =>
              handleDeleteCampaign(campaign, true) // Pass silent=true to prevent individual confirmations
            )
          ).then(() => {
            // Clear selection after operation
            setSelectedCampaigns([]);
            setCampaignsToDelete([]);
            setShowDeleteModal(false);
          });
        }}
        title={`Delete ${campaignsToDelete.length} Campaign${campaignsToDelete.length !== 1 ? 's' : ''}`}
        message={
          campaignsToDelete.some(c => c.status === 'sending')
            ? "Some campaigns cannot be deleted because they are currently sending."
            : `Are you sure you want to delete the selected campaign${campaignsToDelete.length !== 1 ? 's' : ''}? This action cannot be undone.`
        }
      />

      {/* Cool AI Loading Animation */}
      {(aiGenerating || aiRefining) && (
        <div className="fixed inset-0 bg-indigo-900 bg-opacity-80 z-[1000] flex flex-col items-center justify-center backdrop-blur-sm">
          <div className="relative w-24 h-24 mb-8 pulse-glow rounded-full">
            <div className="absolute inset-0 border-4 border-t-indigo-400 border-r-indigo-200 border-b-indigo-500 border-l-indigo-300 rounded-full animate-spin"></div>
            <div className="absolute inset-2 border-4 border-t-purple-400 border-r-purple-200 border-b-purple-500 border-l-purple-300 rounded-full animate-spin animation-delay-150" style={{animationDirection: 'reverse'}}></div>
            <div className="absolute inset-4 border-4 border-t-blue-400 border-r-blue-200 border-b-blue-500 border-l-blue-300 rounded-full animate-spin animation-delay-300"></div>
            <div className="absolute inset-6 border-4 border-t-indigo-400 border-r-indigo-200 border-b-indigo-500 border-l-indigo-300 rounded-full animate-spin animation-delay-500"></div>
          </div>
          <div className="text-white text-lg font-medium mb-3">
            {aiGenerating ? 'Generating Email Content with AI' : 'Refining Email Content with AI'}
          </div>
          {/* Use loadedAiProvider for display */}
          <div className="text-indigo-200 text-sm mb-6">
            {/* Removed display of provider/model */}
            Generating...
          </div>

          <div className="max-w-md bg-white/10 backdrop-blur-sm p-4 rounded-lg text-white text-sm">
            <div className="font-medium mb-1">AI Tip:</div>
            <div>
              {[
                "More detailed prompts generally lead to better AI-generated emails.",
                "Specify your target audience to tailor the message appropriately.",
                "For longer emails, consider breaking them into logical sections.",
                "Being specific about your goal helps the AI craft more effective copy.",
                "The tone setting can dramatically change how your message feels.",
                "Including additional context yields more relevant and personalized content.",
                "Remember to review AI-generated content for brand voice consistency.",
                "For non-English emails, specify the language in your prompt."
              ][Math.floor(Math.random() * 8)]}
            </div>
          </div>

          {/* Progress Bar */}
          <div className="mt-8 w-64 h-2 bg-indigo-800 rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-blue-400 via-indigo-500 to-purple-500 progress-animation rounded-full"></div>
          </div>

          <div className="text-indigo-200 text-xs mt-2 mb-6">
            Approximate time remaining...
          </div>

          {/* Bouncing dots */}
          <div className="flex space-x-1 mt-2">
            <div className="w-2 h-2 bg-white rounded-full animate-bounce"></div>
            <div className="w-2 h-2 bg-white rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
            <div className="w-2 h-2 bg-white rounded-full animate-bounce" style={{animationDelay: '0.4s'}}></div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MailerView;
